import { Box, Chip, Tooltip, Typography } from '@mui/material'
import { FC } from 'react'

// Типы для details
interface OperatorTransferDetails {
  refType: 'operatorId'
  login?: string
  comment?: string
}

interface RouletteGameDetails {
  refType: 'rouletteId'
  gameRatio?: number
  sessionId?: string
}

interface SlotGameDetails {
  refType: 'slotId'
  operationId?: string
}

interface RussianPokerGameDetails {
  refType: 'prp2boxId'
  kind?:
    | 'box_pass'
    | 'box_win'
    | 'box_lose'
    | 'box_tie'
    | 'box_no_game'
    | 'box_bonus_win'
    | 'box_bonus_lose'
    | 'box_insurance_lose'
    | 'box_insurance_win'
  gameRatio?: number
  publicCtx?: {
    gameId?: string
    sessionId?: string
    seatNumber?: number
  }
}

// Маппинг для kind русского покера
const RUSSIAN_POKER_KIND_LABELS: Record<
  string,
  { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default' }
> = {
  box_pass: { label: 'Пас', color: 'default' },
  box_win: { label: 'Выигрыш', color: 'success' },
  box_lose: { label: 'Проигрыш', color: 'error' },
  box_tie: { label: 'Ничья', color: 'warning' },
  box_no_game: { label: 'Нет игры', color: 'default' },
  box_bonus_win: { label: 'Бонус', color: 'success' },
  box_bonus_lose: { label: 'Бонус', color: 'error' },
  box_insurance_lose: { label: 'Страховка', color: 'error' },
  box_insurance_win: { label: 'Страховка', color: 'success' },
}

// Компонент для отображения деталей оператора
const OperatorDetails: FC<{ details: OperatorTransferDetails }> = ({ details }) => {
  const hasComment = details.comment && details.comment.length > 0

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label="Оператор" size="small" color="info" variant="filled" sx={{ fontWeight: 600 }} />
        {details.login && (
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
            {details.login}
          </Typography>
        )}
      </Box>
      {hasComment && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            color: 'text.secondary',
          }}>
          {details.comment}
        </Typography>
      )}
    </Box>
  )
}

// Компонент для отображения деталей рулетки
const RouletteDetails: FC<{ details: RouletteGameDetails; refId?: string }> = ({ details, refId }) => {
  const detailParts: string[] = []

  if (details.sessionId) detailParts.push(`Session: ${details.sessionId}`)
  if (details.gameRatio !== undefined) detailParts.push(`Коэф: ${details.gameRatio}`)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label="Рулетка" size="small" color="warning" variant="filled" sx={{ fontWeight: 600 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
          {refId && `${refId}`}
        </Typography>
      </Box>
      {detailParts.length > 0 && (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {detailParts.join(' • ')}
        </Typography>
      )}
    </Box>
  )
}

// Компонент для отображения деталей слотов
const SlotDetails: FC<{ details: SlotGameDetails; refId?: string }> = ({ details, refId }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label="Слоты" size="small" color="warning" variant="filled" sx={{ fontWeight: 600 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
          {refId && `${refId}`}
        </Typography>
      </Box>
      {(refId || details.operationId) && (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {[details.operationId && `Operaion: ${details.operationId}`].filter(Boolean).join(' • ')}
        </Typography>
      )}
    </Box>
  )
}

// Компонент для отображения деталей русского покера
const RussianPokerDetailsComponent: FC<{ details: RussianPokerGameDetails; refId?: string }> = ({ details, refId }) => {
  const kindInfo = details.kind
    ? RUSSIAN_POKER_KIND_LABELS[details.kind] || { label: details.kind, color: 'default' as const }
    : { label: 'Неизвестно', color: 'default' as const }

  const detailParts: string[] = []
  if (details.publicCtx?.sessionId) detailParts.push(`Session: ${details.publicCtx.sessionId}`)
  if (details.publicCtx?.seatNumber !== undefined) detailParts.push(`Место: ${details.publicCtx.seatNumber}`)
  if (details.gameRatio !== undefined) detailParts.push(`Коэф: ${details.gameRatio}`)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip label="Русский покер" size="small" color="warning" variant="filled" sx={{ fontWeight: 600 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
          {refId && `${refId}`}
        </Typography>
        <Chip label={kindInfo.label} size="small" color={kindInfo.color} variant="outlined" />
      </Box>

      {detailParts.length > 0 && (
        <Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {detailParts.join(' • ')}
        </Typography>
      )}
    </Box>
  )
}

// Основная функция форматирования
export const formatTransactionDetails = (details: any, refId?: string): JSX.Element | null => {
  if (!details || typeof details !== 'object' || Object.keys(details).length === 0) {
    return null
  }

  // Определяем тип транзакции по refType
  switch (details.refType) {
    case 'operatorId':
      return <OperatorDetails details={details as OperatorTransferDetails} />

    case 'rouletteId':
      return <RouletteDetails details={details as RouletteGameDetails} refId={refId} />

    case 'slotId':
      return <SlotDetails details={details as SlotGameDetails} refId={refId} />

    case 'prp2boxId':
      return <RussianPokerDetailsComponent details={details as RussianPokerGameDetails} refId={refId} />

    default:
      // Fallback для неизвестных типов - показываем JSON в tooltip
      return (
        <Tooltip title={<pre style={{ margin: 0, fontSize: '0.75rem' }}>{JSON.stringify(details, null, 2)}</pre>} arrow>
          <Chip label="Неизвестный тип" size="small" color="default" variant="outlined" />
        </Tooltip>
      )
  }
}

// Компонент для колонки
export const TransactionDetailsCell: FC<{ details: any; refId?: string }> = ({ details, refId }) => {
  const content = formatTransactionDetails(details, refId)

  if (!content) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
        —
      </Typography>
    )
  }

  return content
}
